import {
  type ChangeEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useStores } from '~/hooks/useStore';
import { useSearchRecommendations } from '~/pages/SearchResults/hooks/useSearchRecommendations';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import type { Category } from '~/stores/CategoriesStore';
import { SEARCH_MIN_LENGTH, searchStore } from '~/stores/SearchStore';
import resetScroll from '~/utils/resetScroll';

import type { UseSearchMobileReturn } from '../interfaces';

export const useSearchMobile = (): UseSearchMobileReturn => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFocused, setIsFocused] = useState(false);
  const { shopCategories } = useStores();
  const [recommendationsData, selectRecommendation] =
    useSearchRecommendations();

  useEffect(() => {
    analyticsService.track(AnalyticsEvent.CATALOG_SEARCH_VISITED, {
      source: state?.source || 'direct',
    });
    analyticsService.track(AnalyticsEvent.CATALOG_SEARCH_VISITED_BI, {
      source: state?.source || 'direct',
    });
  }, []);

  useEffect(() => {
    const currentQueryString = searchParams.get('q');

    if (!searchStore.queryString && currentQueryString) {
      searchStore.setQuery(currentQueryString ?? '');
      searchStore.paginationRequest.debounceLoadPage(1, currentQueryString);
    } else if (searchStore.queryString) {
      searchParams.set('q', searchStore.queryString);
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  const categories = useMemo(
    () =>
      shopCategories.list
        .reduce((acc: Category[], category) => {
          return [...acc, ...category.subcategory];
        }, [])
        .slice(0, 5)
        .map((category) => ({
          id: category.id,
          name: category.name,
          image: category.previewImageThumb || category.previewImage || '',
        })),
    [shopCategories.list],
  );

  const hasSearchQuery = searchStore.queryString.length >= SEARCH_MIN_LENGTH;
  const isWaitingForResults = hasSearchQuery && searchStore.result === null;
  const showDefaultContent = !hasSearchQuery || isWaitingForResults;
  const showSearchResults =
    hasSearchQuery &&
    searchStore.result !== null &&
    searchStore.result.length > 0;
  const showEmptyState =
    hasSearchQuery &&
    searchStore.result !== null &&
    searchStore.result.length === 0;

  const setQuery = useCallback(
    (str?: string) => {
      if (str?.trim()) {
        searchParams.set('q', str);
      } else {
        searchParams.delete('q');
      }
      setSearchParams(searchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleKeyUp = useCallback(
    (e: KeyboardEvent<HTMLInputElement>): void => {
      if (e.code === 'Enter') {
        e.currentTarget.blur();
      }

      const { value } = e.currentTarget;
      if (!value) {
        return;
      }

      e.preventDefault();
      setQuery(value);
      searchStore.paginationRequest.debounceLoadPage(1, value);
    },
    [setQuery],
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      searchStore.setQuery(e.target.value);
      setQuery(e.target.value);
      searchStore.paginationRequest.debounceLoadPage(1, e.target.value);
    },
    [setQuery],
  );

  const handleReset = useCallback(() => {
    searchStore.reset();
    setQuery();
    resetScroll();
  }, [setQuery]);

  const handleCancel = useCallback(() => {
    window.history.back();
  }, []);

  const handleSelectRecommendation = useCallback(
    (value: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      selectRecommendation(value);
    },
    [selectRecommendation],
  );

  const handleCategoryClick = useCallback(
    (categoryId: string | number) =>
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        navigate(`/c/${categoryId}`);
      },
    [navigate],
  );

  const handleHistoryReset = useCallback(() => {
    searchStore.clearHistory.run();
  }, []);

  const handleLoadMore = useCallback(() => {
    searchStore.paginationRequest.loadMore(searchStore.queryString);
  }, []);

  const hasQuery = searchStore.queryString.length > 0;

  return {
    // Search input state
    isFocused,
    hasQuery,
    setIsFocused,

    // Display states
    hasSearchQuery,
    isWaitingForResults,
    showDefaultContent,
    showSearchResults,
    showEmptyState,

    // Data
    recommendationsData,
    categories,

    // Handlers
    handleKeyUp,
    handleInputChange,
    handleReset,
    handleCancel,
    handleSelectRecommendation,
    handleCategoryClick,
    handleHistoryReset,
    handleLoadMore,
  };
};
